import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { i18n } from './i18nInit';
import snackbar from './snackbar';

/*
 * This utils file contains the method needed to make request to the API.
 *
 * The main method is request, every requet to the API are made by it (except those made by i18n for loading translations files).
 * It takes a mandatory parameter, and 4 optional parameters:
 * - url: the relative URL of the request
 * - method: the HTTP method to use for the request, default is GET
 * - data: the data to send in the body, for POST and PUT requests, or as query string, for GET and DELETE requests
 * - displayError: whether or not to show error snackbars
 * - raw: true if you do not convert the date keys to snake case before making the request, and to object after receiving the response
 *
 * The login method use the request method under the hood, and set the localStorage item if the login successful.
 *
 * The logout method simply remove the token from the localStorage.
 *
 * The isLoggedIn method makes a request to /users/me to check if the user is logged in or not
 */

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface LoginApiResponse {
    token: string;
}

const api = {
    request: (url: string = '', method: HttpMethod = 'GET', data: any = null, displayError: boolean = true, raw: false | 'text' | 'blob' = false, displayApiLogicError: boolean = true) => {
        return new Promise((resolve: (value?: any) => void, reject) => {
            const finalData = raw ? data : (data && snakecaseKeys(data));
            const finalUrl = (['GET', 'DELETE'].includes(method) && finalData) ? (url.replace(/\/$/, '') + '?' + new URLSearchParams(finalData)) : (url.replace(/\/$/, ''));

            // @ts-ignore
            fetch(process.env.REACT_APP_API_HOST + (window.pfcCo2Tool !== undefined ? '/cors' : '') + finalUrl, {
                method,
                // @ts-ignore
                headers: window.pfcCo2Tool === undefined ? {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('access_token'),
                    'Accept-Language': (i18n?.isInitialized ? i18n.t('language_code') : 'it-IT'),
                    'X-Frontend-Version': process.env.REACT_APP_BUILD_VERSION
                } : {},
                // @ts-ignore
                ...(window.pfcCo2Tool === undefined && { credentials: 'include' }),
                ...(finalData && ['POST', 'PUT'].includes(method) && { body: JSON.stringify(finalData) })
            }).then(res => {
                const { status } = res;
                const success = (status >= 200 && status <= 299);
                const afterJsonDecode = success ? resolve : reject;

                if (status === 204) {
                    resolve();
                }
                else {
                    if (raw === 'text' && success) {
                        res.text().then((output: any) => {
                            resolve(output);
                        }).catch(() => {
                            if (displayError) {
                                snackbar.error(i18n.t('errors.api_request_failed', { code: status }));
                            }
                        });
                    }
                    else if (raw === 'blob' && success) {
                        res.blob().then((output: any) => {
                            resolve(output);
                        }).catch(() => {
                            if (displayError) {
                                snackbar.error(i18n.t('errors.api_request_failed', { code: status }));
                            }
                        });
                    }
                    else {
                        res.json().then(json => {
                            if (!success && json.force_logout && url !== '/users/me') {
                                api.logout();
                                window.location.reload();
                            }

                            if (!success && displayError && json) {
                                // if the request is failed and displayError is true, show errors using snackbar
                                if (json.error) {
                                    // generic errors are in the error attribute
                                    if (displayApiLogicError) {
                                        snackbar.error(json.error);
                                    }
                                }
                                else {
                                    // in case of validation errors, every field has its error message, so we cycle and display all of them
                                    Object.keys(json).map((key: string) => {
                                        return snackbar.error(json[key]);
                                    });
                                }
                            }

                            afterJsonDecode(camelcaseKeys(json, { deep: true }));
                        }).catch(() => {
                            if (displayError) {
                                snackbar.error(i18n.t('errors.api_request_failed', { code: status }));
                            }

                            reject();
                        });
                    }
                }
            }).catch(() => {
                snackbar.error(i18n.t('errors.api_request_failed_no_internet'), { autoHideDuration: 10000 });
                reject();
            });
        });
    },
    login: (username: string, password: string, rememberMe: boolean) => {
        return new Promise((resolve, reject) => {
            api.request('/auth/login', 'POST', { username, password, persistentSession: rememberMe }, false).then((res: LoginApiResponse) => {
                const { token } = res;
                localStorage.setItem('access_token', token);
                resolve(true);
            }).catch((res) => {
                reject(res);
            });
        });
    },
    logout: () => {
        localStorage.removeItem('access_token');
    },
    isLoggedIn: () => {
        return new Promise((resolve) => {
            api.request('/users/me').then(() => {
                resolve(true);
            }).catch(() => {
                resolve(false);
            });
        });
    }
};

export default api;
